import React, { useState, useEffect } from 'react';
import { Eye, Package, Edit, Archive, Plus, Search, Filter } from '../../components/Icons';
import { useAuth } from '../../contexts/AuthContext';
import Navigation from '../../components/Navigation';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const Tenants = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTenants, setTotalTenants] = useState(0);
  const [statusFilter, setStatusFilter] = useState('active'); // default: hide archived tenants
  const [minInvoices, setMinInvoices] = useState('');
  const [maxInvoices, setMaxInvoices] = useState('');
  const [tokenFilter, setTokenFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkArchiving, setBulkArchiving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'Pakistan',
    postal_code: '',
    tax_number: '',
    seller_ntn_cnic: '',
    seller_province: '',
    seller_business_name: '',
    seller_address: '',
    subscription_status: 'active',
    subscription_plan: 'basic',
    subscription_start_date: '',
    subscription_end_date: '',
    is_active: true
  });

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Search/filter changes reset to page 1 - if we're already on page 1 that
  // won't re-trigger the effect above, so fetch directly in that case.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchTenants();
      }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, minInvoices, maxInvoices, tokenFilter]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 10
      });
      if (searchTerm) params.set('search', searchTerm);
      if (statusFilter) params.set('status_filter', statusFilter);
      if (minInvoices !== '') params.set('min_invoices', minInvoices);
      if (maxInvoices !== '') params.set('max_invoices', maxInvoices);
      if (tokenFilter) params.set('token_filter', tokenFilter);

      const response = await apiGet(`/api/admin/tenants?${params}`);

      if (response.ok) {
        const data = response.data;
        setTenants(data.tenants);
        setTotalPages(data.pages);
        setTotalTenants(data.total);
        setSelectedIds([]); // selection refers to the old page's rows - stale once they change
      } else {
        console.error('Failed to fetch tenants');
        // Fallback to mock data
        setTenants([
          {
            id: 1,
            name: 'ABC Trading Co.',
            business_name: 'ABC Trading Company',
            email: 'abc@example.com',
            phone: '+92-300-1234567',
            address: '123 Business Street, Lahore',
            city: 'Lahore',
            state: 'Punjab',
            country: 'Pakistan',
            postal_code: '54000',
            tax_number: '1234567890123',
            seller_ntn_cnic: '1234567890123',
            seller_province: 'Punjab',
            seller_business_name: 'ABC Trading Company',
            seller_address: '123 Business Street, Lahore',
            subscription_status: 'active',
            subscription_plan: 'premium',
            subscription_start_date: '2024-01-01',
            subscription_end_date: '2024-12-31',
            is_active: true,
            user_count: 5,
            invoice_count: 125
          },
          {
            id: 2,
            name: 'XYZ Enterprises',
            business_name: 'XYZ Enterprises Ltd.',
            email: 'xyz@example.com',
            phone: '+92-300-9876543',
            address: '456 Commerce Road, Karachi',
            city: 'Karachi',
            state: 'Sindh',
            country: 'Pakistan',
            postal_code: '75000',
            tax_number: '9876543210987',
            seller_ntn_cnic: '9876543210987',
            seller_province: 'Sindh',
            seller_business_name: 'XYZ Enterprises Ltd.',
            seller_address: '456 Commerce Road, Karachi',
            subscription_status: 'pending',
            subscription_plan: 'basic',
            subscription_start_date: '2024-02-01',
            subscription_end_date: '2024-07-31',
            is_active: true,
            user_count: 3,
            invoice_count: 78
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingTenant 
        ? `/api/admin/tenants/${editingTenant.id}`
        : `/api/admin/tenants`;
      
      const method = editingTenant ? 'PUT' : 'POST';
      
      // Map frontend fields to backend expected fields
      const tenantData = {
        name: formData.name,
        business_type: formData.business_name,
        contact_email: formData.email,
        contact_phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country
      };

      const response = await apiPost(url, tenantData);

      if (response.ok) {
        alert(editingTenant ? 'Tenant updated successfully!' : 'Tenant created successfully!');
        setShowModal(false);
        setEditingTenant(null);
        resetForm();
        fetchTenants();
      } else {
        const error = response.data;
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving tenant:', error);
      alert('Error saving tenant');
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      business_name: tenant.business_name,
      email: tenant.email,
      phone: tenant.phone,
      address: tenant.address,
      city: tenant.city,
      state: tenant.state,
      country: tenant.country,
      postal_code: tenant.postal_code,
      tax_number: tenant.tax_number,
      seller_ntn_cnic: tenant.seller_ntn_cnic,
      seller_province: tenant.seller_province,
      seller_business_name: tenant.seller_business_name,
      seller_address: tenant.seller_address,
      subscription_status: tenant.subscription_status,
      subscription_plan: tenant.subscription_plan,
      subscription_start_date: tenant.subscription_start_date,
      subscription_end_date: tenant.subscription_end_date,
      is_active: tenant.is_active
    });
    setShowModal(true);
  };

  const handleArchive = async (tenantId) => {
    if (!window.confirm('Archive this tenant? Users will not be able to login until it is unarchived.')) return;

    try {
      const response = await apiDelete(`/api/admin/tenants/${tenantId}`);

      if (response.ok) {
        alert('Tenant archived successfully!');
        fetchTenants();
      } else {
        const errorMsg = response.data?.detail || 'Failed to archive tenant';
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error archiving tenant:', error);
      alert('Error archiving tenant');
    }
  };

  const toggleSelect = (tenantId) => {
    setSelectedIds((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId]
    );
  };

  const selectableIds = tenants.filter((t) => t.is_active).map((t) => t.id);
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    setSelectedIds(allSelectableSelected ? [] : selectableIds);
  };

  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Archive ${selectedIds.length} selected tenant(s)? Their users will not be able to login until unarchived.`)) return;

    setBulkArchiving(true);
    try {
      const results = await Promise.allSettled(
        selectedIds.map((id) => apiDelete(`/api/admin/tenants/${id}`))
      );
      const failed = results.filter((r) => r.status === 'rejected' || !r.value?.ok).length;
      const succeeded = results.length - failed;

      if (failed > 0) {
        alert(`Archived ${succeeded} tenant(s). ${failed} failed (they may already be archived) - check the list and retry those individually.`);
      } else {
        alert(`Archived ${succeeded} tenant(s) successfully!`);
      }
      fetchTenants();
    } catch (error) {
      console.error('Error bulk archiving tenants:', error);
      alert('Error archiving selected tenants');
    } finally {
      setBulkArchiving(false);
    }
  };

  const handleReactivate = async (tenantId) => {
    if (!window.confirm('Are you sure you want to reactivate this tenant?')) return;

    try {
      const response = await apiPut(`/api/admin/tenants/${tenantId}/reactivate`);

      if (response.ok) {
        alert('Tenant reactivated successfully!');
        fetchTenants();
      } else {
        const errorMsg = response.data?.detail || 'Failed to reactivate tenant';
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error reactivating tenant:', error);
      alert('Error reactivating tenant');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      business_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: 'Pakistan',
      postal_code: '',
      tax_number: '',
      seller_ntn_cnic: '',
      seller_province: '',
      seller_business_name: '',
      seller_address: '',
      subscription_status: 'active',
      subscription_plan: 'basic',
      subscription_start_date: '',
      subscription_end_date: '',
      is_active: true
    });
  };

  const openCreateModal = () => {
    setEditingTenant(null);
    resetForm();
    setShowModal(true);
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubscriptionPlanColor = (plan) => {
    switch (plan) {
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'basic': return 'bg-blue-100 text-blue-800';
      case 'enterprise': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <Navigation>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Tenant Management</h1>
                <p className="text-gray-600">Manage system tenants and subscriptions</p>
              </div>
            <button
              onClick={openCreateModal}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              <span>Add Tenant</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search tenants by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="active">Active (hide archived)</option>
                <option value="inactive">Archived only</option>
                <option value="">All (incl. archived)</option>
              </select>
            </div>
            <button
              onClick={fetchTenants}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Refresh
            </button>
          </div>
          <div className="flex items-center flex-wrap gap-4 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Invoices</label>
              <input
                type="number"
                min="0"
                placeholder="Min"
                value={minInvoices}
                onChange={(e) => setMinInvoices(e.target.value)}
                className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-gray-400">-</span>
              <input
                type="number"
                min="0"
                placeholder="Max"
                value={maxInvoices}
                onChange={(e) => setMaxInvoices(e.target.value)}
                className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">FBR token</label>
              <select
                value={tokenFilter}
                onChange={(e) => setTokenFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Any</option>
                <option value="configured">Configured</option>
                <option value="not_configured">Not configured</option>
              </select>
            </div>
            {(minInvoices !== '' || maxInvoices !== '' || tokenFilter) && (
              <button
                onClick={() => { setMinInvoices(''); setMaxInvoices(''); setTokenFilter(''); }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear these filters
              </button>
            )}
          </div>
        </div>

        {/* Tenants Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Tenant List</h2>
            {selectedIds.length > 0 && (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">{selectedIds.length} selected</span>
                <button
                  onClick={handleBulkArchive}
                  disabled={bulkArchiving}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  <Archive className="h-4 w-4" />
                  <span>{bulkArchiving ? 'Archiving...' : `Archive Selected (${selectedIds.length})`}</span>
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      title="Select all active tenants on this page"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(tenant.id)}
                        onChange={() => toggleSelect(tenant.id)}
                        disabled={!tenant.is_active}
                        title={tenant.is_active ? 'Select for bulk archive' : 'Already archived'}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-30"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Package className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                          <div className="text-sm text-gray-600">{tenant.business_type || tenant.seller_business_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{tenant.city}, {tenant.state}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tenant.contact_email || tenant.email}</div>
                      <div className="text-sm text-gray-600">{tenant.contact_phone || tenant.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubscriptionStatusColor(tenant.subscription_status)}`}>
                            {tenant.subscription_status}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubscriptionPlanColor(tenant.subscription_plan)}`}>
                            {tenant.subscription_plan}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 mt-2">
                          <select
                            value={tenant.subscription_status || 'trial'}
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              await apiPut(`/api/admin/tenants/${tenant.id}`, { subscription_status: newStatus });
                              fetchTenants();
                            }}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="trial">trial</option>
                            <option value="pending">pending</option>
                            <option value="active">active</option>
                            <option value="suspended">suspended</option>
                          </select>
                          <select
                            value={tenant.subscription_plan || 'basic'}
                            onChange={async (e) => {
                              const newPlan = e.target.value;
                              await apiPut(`/api/admin/tenants/${tenant.id}`, { subscription_plan: newPlan });
                              fetchTenants();
                            }}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="basic">basic</option>
                            <option value="premium">premium</option>
                            <option value="enterprise">enterprise</option>
                          </select>
                          {tenant.subscription_status === 'pending' && (
                            <button
                              onClick={async () => {
                                await apiPost(`/api/admin/tenants/${tenant.id}/approve`);
                                fetchTenants();
                              }}
                              className="text-green-600 hover:text-green-900 text-xs"
                              title="Approve Tenant"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Package className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{tenant.user_count}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Package className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{tenant.invoice_count}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {tenant.is_active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(tenant)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Tenant"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {tenant.is_active ? (
                          <button
                            onClick={() => handleArchive(tenant.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Archive Tenant"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(tenant.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Unarchive Tenant"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tenants.length === 0 && (
              <div className="px-6 py-8 text-center text-sm text-gray-500">
                No tenants match this search/filter.
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {totalTenants > 0
                ? `Showing page ${currentPage} of ${totalPages} (${totalTenants} tenants total)`
                : 'No tenants found'}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tenant Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      value={formData.business_name}
                      onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State/Province *
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Tax Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Number
                      </label>
                      <input
                        type="text"
                        value={formData.tax_number}
                        onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seller NTN/CNIC
                      </label>
                      <input
                        type="text"
                        value={formData.seller_ntn_cnic}
                        onChange={(e) => setFormData({...formData, seller_ntn_cnic: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Subscription</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subscription Plan
                      </label>
                      <select
                        value={formData.subscription_plan}
                        onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="basic">Basic</option>
                        <option value="premium">Premium</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subscription Status
                      </label>
                      <select
                        value={formData.subscription_status}
                        onChange={(e) => setFormData({...formData, subscription_status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="expired">Expired</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={formData.subscription_start_date}
                        onChange={(e) => setFormData({...formData, subscription_start_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={formData.subscription_end_date}
                        onChange={(e) => setFormData({...formData, subscription_end_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Active Tenant
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingTenant ? 'Update Tenant' : 'Create Tenant'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </Navigation>
  );
};

export default Tenants; 